/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Link, useNavigate } from 'react-router-dom'

import { Alert, Button, Checkbox, PageContainer, PageHeader } from '@/components/ui'
import { useApi } from '@/hooks'
import { getAuthProviderLabel } from '@/utils/authProvider'
import logger from '@/utils/logger'

import { AdminHeaderCells, AdminRowCells, type AdminColumns, useAdminTable } from './adminTable'
import { assetGroupsLabel, inAssetGroup } from './assetGroupLabels'
import { LoadingPanel } from './LoadingPanel'
import { AssetGroupFilter } from './permissions/AssetGroupFilter'
import { CustomerDropdown } from './permissions/CustomerDropdown'
import { useAdminAssetGroups } from './permissions/useAdminAssetGroups'
import { useAdminCustomers } from './permissions/useAdminCustomers'
import type { AdminUserSummary } from './types'

const log = logger({ category: 'AdminUsersPage' })

const fullName = (user: Pick<AdminUserSummary, 'firstName' | 'lastName'>) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ')

/** Rows per page. The full list is already in memory; paging only keeps the table readable. */
const PAGE_SIZE = 50

const AdminUsersPage: React.FC = () => {
  const { api } = useApi()
  const { customers, customerName: customerNameOf } = useAdminCustomers()
  const { groups: assetGroups } = useAdminAssetGroups()
  const navigate = useNavigate()
  const [users, setUsers] = useState<AdminUserSummary[]>([])
  const [error, setError] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState('')
  const [companyFilter, setCompanyFilter] = useState<number>()
  const [assetGroupFilter, setAssetGroupFilter] = useState<number>()
  const [page, setPage] = useState(1)
  // Bulk permissions: the checkbox column only appears once the admin asks for it, and the
  // selection is thrown away on leaving so a stale pick can't ride into a later session.
  const [selectMode, setSelectMode] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(() => new Set())

  const companyName = useCallback((companyId: number | null): string => customerNameOf(companyId), [customerNameOf])

  const load = useCallback(async () => {
    if (!api) return
    setIsLoading(true)
    try {
      const response = await api.get<AdminUserSummary[]>({
        path: '/app/admin/users',
        origin: 'AdminUsersPage#load',
      })
      if (response.error) {
        setError(response.message || 'Could not load users')
      } else {
        setError('')
        setUsers(response.data ?? [])
      }
    } catch (ex) {
      log.error(ex)
      setError('Could not load users')
    } finally {
      setIsLoading(false)
    }
  }, [api])

  useEffect(() => {
    void load()
  }, [load])

  const groupLabel = useCallback((user: AdminUserSummary) => assetGroupsLabel(user, assetGroups), [assetGroups])

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase()
    const byCompany = companyFilter === undefined ? users : users.filter((u) => u.companyId === companyFilter)
    const group = assetGroups.find((g) => g.companyId === assetGroupFilter)
    const byGroup = group ? byCompany.filter((u) => inAssetGroup(u, group)) : byCompany
    if (!q) return byGroup
    return byGroup.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        u.userName.toLowerCase().includes(q) ||
        fullName(u).toLowerCase().includes(q) ||
        u.uid.toLowerCase().includes(q) ||
        (u.geotabEmail ?? '').toLowerCase().includes(q) ||
        (u.geotabDatabase ?? '').toLowerCase().includes(q) ||
        companyName(u.companyId).toLowerCase().includes(q) ||
        groupLabel(u).toLowerCase().includes(q) ||
        getAuthProviderLabel(u.uid).toLowerCase().includes(q)
    )
  }, [users, filter, companyFilter, assetGroupFilter, assetGroups, companyName, groupLabel])

  const columns = useMemo<AdminColumns<AdminUserSummary>>(() => {
    // A company that is in the customer table links to its page; one that is not yet
    // (the owner adds them as they are onboarded) shows the id placeholder.
    const companyCell = (companyId: number | null) => {
      if (companyId === null) return <span className="text-base-content/40">—</span>
      const customer = customers.find((c) => c.customerId === companyId)
      if (!customer) return companyName(companyId)
      return (
        <Link
          className="link link-primary"
          to={`/admin/companies/${String(companyId)}`}
          data-testid={`company-link-${String(companyId)}`}
        >
          {customer.name}
        </Link>
      )
    }
    return [
      {
        id: 'email',
        header: 'Email',
        accessorFn: (user) => user.email || fullName(user) || user.userName,
        cell: ({ row, getValue }) => (
          <Link className="link link-primary" to={`/admin/users/${encodeURIComponent(row.original.uid)}`}>
            {getValue<string>()}
          </Link>
        ),
      },
      { id: 'name', header: 'Name', accessorFn: (user) => fullName(user) || undefined },
      { id: 'userName', header: 'Username', accessorFn: (user) => user.userName || undefined },
      {
        id: 'company',
        header: 'Company',
        accessorFn: (user) => (user.companyId === null ? undefined : companyName(user.companyId)),
        meta: { className: 'whitespace-nowrap' },
        cell: ({ row }) => companyCell(row.original.companyId),
      },
      {
        id: 'assetGroups',
        header: 'Asset groups',
        accessorFn: (user) => groupLabel(user) || undefined,
        meta: {
          className: 'max-w-64 truncate',
          tdProps: (user: AdminUserSummary) => ({ title: groupLabel(user) }),
        },
      },
      {
        // The same person often has both an Active Directory and a legacy Cooltrax
        // profile with identical email and username, so without this the two rows are
        // indistinguishable when picking one to map.
        id: 'source',
        header: 'Source',
        accessorFn: (user) => getAuthProviderLabel(user.uid),
        meta: { className: 'whitespace-nowrap text-base-content/70' },
      },
    ]
  }, [customers, companyName, groupLabel])

  // A new sort starts back at page 1, as a filter change does.
  const table = useAdminTable(visible, columns, { onSortingChange: () => setPage(1) })
  const sortedRows = table.getRowModel().rows

  // Paging over the filtered set. A filter change resets to page 1 (see the handlers);
  // clamping covers the list shrinking underneath the current page.
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE))
  const currentPage = Math.min(page, pageCount)
  const pageRows = useMemo(
    () => sortedRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [sortedRows, currentPage]
  )
  const firstRow = visible.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const lastRow = Math.min(currentPage * PAGE_SIZE, visible.length)

  const changeFilter = (value: string) => {
    setFilter(value)
    setPage(1)
  }
  const changeCompanyFilter = (id?: number) => {
    setCompanyFilter(id)
    // The group list follows the company, so a group from another company can't linger.
    if (id !== undefined && assetGroups.find((g) => g.companyId === assetGroupFilter)?.customerId !== id) {
      setAssetGroupFilter(undefined)
    }
    setPage(1)
  }
  const changeAssetGroupFilter = (id?: number) => {
    setAssetGroupFilter(id)
    setPage(1)
  }

  const visibleUids = useMemo(() => visible.map((u) => u.uid), [visible])
  const pageUids = useMemo(() => pageRows.map((row) => row.original.uid), [pageRows])
  const pageSelectedCount = pageUids.filter((uid) => selected.has(uid)).length
  const allPageSelected = pageUids.length > 0 && pageSelectedCount === pageUids.length
  const allVisibleSelected = visibleUids.length > 0 && visibleUids.every((uid) => selected.has(uid))

  const toggleSelectMode = () => {
    setSelectMode((on) => !on)
    setSelected(new Set())
  }

  const toggleOne = (uid: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) next.add(uid)
      else next.delete(uid)
      return next
    })
  }

  // Only rows the admin can see: the header checkbox works on the current page, the bar's
  // "Select all N matching" on the whole filtered set — never on users hidden by a filter.
  const toggleMany = (uids: string[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      for (const uid of uids) {
        if (checked) next.add(uid)
        else next.delete(uid)
      }
      return next
    })
  }

  const columnCount = selectMode ? 7 : 6

  return (
    <PageContainer>
      <Link to="/admin" className="link link-hover mb-2 inline-block text-sm">
        ← Admin
      </Link>
      {/* No count while loading — "0 Insights users" over a spinner reads as an empty system. */}
      <PageHeader
        title="Users"
        subtitle={isLoading ? undefined : `${users.length} Insights ${users.length === 1 ? 'user' : 'users'}`}
      />

      {error && <Alert message={error} type="error" />}

      <div className="mb-3 flex items-end gap-3">
        <input
          className="input input-bordered input-sm w-full max-w-md"
          placeholder="Filter by email, name, username, company, asset group, id or Geotab mapping"
          value={filter}
          onChange={(e) => changeFilter(e.target.value)}
          aria-label="Filter users"
        />
        <div className="w-64" data-testid="company-filter">
          <CustomerDropdown value={companyFilter} onChange={changeCompanyFilter} />
        </div>
        <div className="w-64">
          <AssetGroupFilter
            groups={assetGroups}
            customerId={companyFilter}
            value={assetGroupFilter}
            onChange={changeAssetGroupFilter}
          />
        </div>
        {(companyFilter !== undefined || assetGroupFilter !== undefined) && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              changeCompanyFilter(undefined)
              changeAssetGroupFilter(undefined)
            }}
          >
            Clear
          </button>
        )}
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            className={`btn btn-sm ${selectMode ? 'btn-active' : 'btn-outline'}`}
            onClick={toggleSelectMode}
            aria-pressed={selectMode}
          >
            {selectMode ? 'Done selecting' : 'Bulk Update Permissions'}
          </button>
          <Link to="/admin/users/import" className="btn btn-outline btn-sm">
            Import users
          </Link>
          <Link to="/admin/users/new" className="btn btn-primary btn-sm">
            Add user
          </Link>
        </div>
      </div>

      {selectMode && (
        <div
          className="mb-2 flex flex-wrap items-center gap-3 rounded-lg bg-base-200 px-3 py-2"
          data-testid="selection-bar"
        >
          <span className="text-sm">{selected.size} selected</span>
          <Button
            variation="primary"
            size="sm"
            isDisabled={selected.size === 0}
            handleClick={() => navigate('/admin/users/permissions', { state: { uuids: [...selected] } })}
          >
            Set Permissions
          </Button>
          {!allVisibleSelected && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleMany(visibleUids, true)}>
              Select all {visible.length} matching
            </button>
          )}
          {selected.size > 0 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <LoadingPanel label="Loading users…" />
      ) : (
        <div className="overflow-x-auto rounded-lg bg-base-100 shadow">
          <table className="table table-sm table-zebra">
            <thead>
              <tr>
                {selectMode && (
                  <th className="w-10">
                    <Checkbox
                      id="select-all-users"
                      label={<span className="sr-only">Select this page</span>}
                      checked={allPageSelected}
                      indeterminate={pageSelectedCount > 0 && !allPageSelected}
                      onChange={(checked) => toggleMany(pageUids, checked)}
                      data-testid="select-all"
                    />
                  </th>
                )}
                <AdminHeaderCells table={table} />
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row) => {
                const user = row.original
                return (
                  <tr key={user.uid}>
                    {selectMode && (
                      <td>
                        <Checkbox
                          id={`select-${user.uid}`}
                          label={<span className="sr-only">Select {user.email}</span>}
                          checked={selected.has(user.uid)}
                          onChange={(checked) => toggleOne(user.uid, checked)}
                          data-testid={`select-${user.uid}`}
                        />
                      </td>
                    )}
                    <AdminRowCells row={row} />
                  </tr>
                )
              })}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="p-4 text-center italic text-base-content/60">
                    No users match that filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {pageCount > 1 && (
            <div className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid="pagination">
              <span>
                {firstRow}–{lastRow} of {visible.length}
              </span>
              <div className="join">
                <button
                  type="button"
                  className="btn btn-sm join-item"
                  disabled={currentPage === 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn btn-sm join-item"
                  disabled={currentPage === pageCount}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </PageContainer>
  )
}

export default AdminUsersPage
